/**
 * PEOPLE AND SIGN-IN, at `/account/people` (slice A3), in place of slice A1's short page. No
 * artboard in `faithfully-kept-business/design/home/` draws it yet: it is built from the Settings
 * canvas's Settings-People artboard, drawn in today's look, in the current tokens and parts, with the
 * asks ops's section 12.4 made live on 2026-10-17 in place of the artboard's "please call" line, and
 * goes on business issue 13's list. The words are `PEOPLE` and `PEOPLE_SHEETS` in `words.ts`, NEW for
 * the owner to read.
 *
 * - "BACK TO ACCOUNT", then "Account" over "People and sign-in" for a client, or "Sign-in" for
 *   anyone else, whose heading takes the focus on arrival as every place's does.
 * - YOUR HOUSEHOLD, a client's alone: her own row first, "Rosa Alvarez (you)", then the others in the
 *   Worker's order, those who can sign in, those invited, then the rest. Each by the name the office
 *   typed, ops's word for her and what her door lets her do, and whether she can sign in, when ops
 *   invited her, or neither. Never an address, a number or an id: a person is named for an ask by
 *   home's own reference, which only the request door turns back into ops's id.
 * - THE ASKS, each the office's to decide and each behind a fresh code (`components/PeopleSheets.tsx`):
 *   "Ask to add someone" under the household while the Worker offers it; on another person's row,
 *   "Ask to change what they see" and "Ask to take them off"; on her own, "Ask to change my name,
 *   email or mobile" and "Ask to remove my access to this page", which the household's only client
 *   is never offered, since ops refuses it (`409 last_client`). Each is said as asked, "Sent to the
 *   office.", then waiting, then the office's own words, or that it could not be done where it sent
 *   none, until ops's push makes it so (CLAUDE.md binding rule 1); one ops accepted is not said,
 *   since the next push shows it done. Every ask of hers about a person is said on that person's
 *   row, one of each kind (round six, slice S2).
 * - WHAT A WAITING ASK HOLDS BACK IS SAID, never a button gone without a word (round six, slice S2):
 *   under her own ask to add someone, that she can ask to add another once the office has answered
 *   it (ops holds one a household); on every other row, while her ask to take someone off, or to
 *   change what someone sees, waits, that she can ask the same about this person once the office
 *   has answered hers, by the name it is about; and under the household while somebody else's ask
 *   holds one, that it does.
 * - A MEMBER is shown her own row and its two asks, and nobody else on the household.
 * - WHERE YOU ARE SIGNED IN: this browser first, then her other live sessions, most recently used
 *   first, by words for the phone or computer from a fixed list and the days each began and was last
 *   used, then how many more. "Sign out everywhere else" while another is live, which ends every
 *   other one of hers, says so with the focus and reads the list again; "Signed in here only." alone;
 *   and "Sign out here", the shipped sign-out (`components/SignOut.tsx`). Under the heading, that
 *   there is no password and a code is emailed each time, or emailed or texted where she signed in
 *   by her mobile number (home #11), whose fresh codes are then texted to it.
 * - SIGNING OUT EVERYWHERE ELSE ASKS FOR A FRESH CODE FIRST (slice P2 of the pre-launch fixes), so a
 *   copy of her cookie can never end the sessions she signs in with. The press goes to the Worker
 *   first, as every ask's does (round six, slice S1); where it answers `403 step_up_required` and
 *   ends nothing, a sheet named by the button holds the other asks' code step (`FreshCode.tsx`),
 *   with its own line of why, and once the code works the same press goes again. Closed, the sheet
 *   has ended nothing, and the focus goes back to the button.
 * - It is read from GET /api/account/people. A 401 is the door, a 404 the page that says to call,
 *   and a read that does not go through says so with the phone.
 */
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  getAccountPeople,
  signOutOthers,
  type OthersSignedOut,
  type PeopleAsk,
  type PeoplePlace,
  type PersonRow,
  type SessionShown,
  type SignedInWith,
} from '../api.ts';
import { GHOST_BUTTON, TEXT_LINK } from '../components/buttons.ts';
import { FreshCode, type ProvedCame } from '../components/FreshCode.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import {
  AddPersonSheet,
  ChangeHomesSheet,
  ContactSheet,
  LastClient,
  LeaveSheet,
  TakeOffSheet,
  type PeopleSheetProps,
} from '../components/PeopleSheets.tsx';
import { Phone } from '../components/Phone.tsx';
import { focusPageHeading } from '../components/RouteFocus.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { Shell } from '../components/Shell.tsx';
import { SignOutFailed, useSignOut } from '../components/SignOut.tsx';
import { askingFor, type Asking } from '../lib/asks.ts';
import { noFreshCode } from '../lib/fresh.ts';
import { askLine } from '../lib/people.ts';
import {
  ACCOUNT,
  andMore,
  changeAsWell,
  deviceWords,
  FAILED_BEFORE_PHONE,
  invitedLine,
  PEOPLE,
  personLine,
  REST_ON_ITS_WAY,
  takeOffAsWell,
  thisOne,
  usedLine,
  youAskedToAdd,
  yourName,
} from '../words.ts';
import { AccountPage } from './Account.tsx';

export function AccountPeople(signed: Signed) {
  return (
    <Guarded load={getAccountPeople} signed={signed} at={null} heading={ACCOUNT.peopleAndSignIn}>
      {(place, reload) => <PeopleDrawn place={place} signed={signed} reload={reload} />}
    </Guarded>
  );
}

/**
 * Which ask's sheet is open: one about the household, about her, or about one person by reference,
 * with that person as the row drew her when the sheet opened, which keeps the sheet drawn where a
 * read since no longer lists her, so what it says of the door's 404 is never taken away in silence.
 */
type AskOpened =
  | { readonly kind: 'add' }
  | { readonly kind: 'contact' }
  | { readonly kind: 'leave' }
  | { readonly kind: 'change'; readonly ref: string; readonly seen: PersonRow }
  | { readonly kind: 'remove'; readonly ref: string; readonly seen: PersonRow };

/** Which sheet is open: an ask's, or the code step of signing out everywhere else (slice P2). */
type Opened = AskOpened | { readonly kind: 'elsewhere' };

/** The key the button that signs out everywhere else is held by, so the focus can come back to it. */
const ELSEWHERE = 'elsewhere';

/** The key an ask is held and said by: its kind, and the person's reference where it names one. */
const keyOf = (opened: Opened): string =>
  opened.kind === 'change' || opened.kind === 'remove'
    ? `${opened.kind}:${opened.ref}`
    : opened.kind;

/** The ask a row's ask is, by the seam's type. */
const ROW_ASKS: Readonly<
  Record<string, { readonly kind: 'change' | 'remove'; readonly words: string }>
> = {
  'person.change': { kind: 'change', words: PEOPLE.askedToChange },
  'person.remove': { kind: 'remove', words: PEOPLE.askedToTakeOff },
};

/** An ask that waits on the office: sent from home, or had by ops and not decided. */
const waiting = (ask: PeopleAsk | undefined) => ask?.state === 'sent' || ask?.state === 'received';

/** An ask the door has just written from this page, said as sent until the page is read again. */
interface SentHere {
  readonly key: string;
  readonly place: PeoplePlace;
  /** The name she gave, for a person to add. */
  readonly name?: string;
}

/** Where the focus goes once a sheet has gone: back to what opened it, or to what was sent. */
type FocusTo = { readonly opener: string } | { readonly ask: string } | null;

const NAME = 'text-body font-semibold text-pretty text-text-body';
const LINE = 'mt-0.5 text-small text-pretty text-text-body';
const MUTED = 'text-small text-pretty text-text-muted';
const CARD =
  'divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-raised px-5';
const ROW = 'flex flex-col py-3.5';
const SECTION_HEADING = 'font-serif text-h3 font-semibold text-text-body';

interface DrawnProps {
  readonly place: PeoplePlace;
  readonly signed: Signed;
  readonly reload: () => void;
}

function PeopleDrawn({ place, signed, reload }: DrawnProps) {
  const client = place.household.payments === true;
  const [opened, setOpened] = useState<Opened | null>(null);
  const [sentHere, setSentHere] = useState<SentHere | null>(null);
  const [focusTo, setFocusTo] = useState<FocusTo>(null);
  /** Each ask's being out and its keys, held by the page for as long as it is shown. */
  const asking = useRef(new Map<string, Asking>());
  /** The button that opens each sheet, so the focus can come back to it. */
  const openers = useRef(new Map<string, HTMLButtonElement>());
  /** Where each ask is said, so the focus can go to it once it is sent. */
  const said = useRef(new Map<string, HTMLElement>());

  useEffect(() => {
    if (focusTo === null) return;
    const target =
      'opener' in focusTo ? openers.current.get(focusTo.opener) : said.current.get(focusTo.ask);
    // Once: what opened a sheet may have gone with a read again that holds its ask, or that no
    // longer lists the person it was about, and then the page's heading takes the focus.
    if (target?.isConnected) target.focus();
    else if ('opener' in focusTo) focusPageHeading();
    setFocusTo(null);
  }, [focusTo]);

  /** The ask the door has just written from this page, while the page is not yet read again. */
  const local = sentHere !== null && sentHere.place === place ? sentHere : null;
  const sentNow = (key: string) => local?.key === key;

  const opener = (key: string) => (button: HTMLButtonElement | null) => {
    if (button) openers.current.set(key, button);
    else openers.current.delete(key);
  };
  const sayingAt = (key: string) => (element: HTMLElement | null) => {
    if (element) said.current.set(key, element);
    else said.current.delete(key);
  };
  const open = (next: Opened) => {
    setFocusTo(null);
    setOpened(next);
  };

  const sheetProps = (key: string): PeopleSheetProps => ({
    household: place.household.id,
    signedInWith: signed.signedInWith,
    asking: askingFor(asking.current, key),
    onClose: () => {
      setOpened(null);
      setFocusTo({ opener: key });
    },
    onSent: (_id, asked) => {
      setOpened(null);
      const name = typeof asked.display_name === 'string' ? asked.display_name : undefined;
      setSentHere({ key, place, ...(name === undefined ? {} : { name }) });
      setFocusTo({ ask: key });
      reload();
    },
    onSignedOut: signed.onSignedOut,
    onStale: reload,
  });

  // Signing out everywhere else, whose code step is a sheet the page draws over itself (slice P2):
  // opened where the Worker asks for a code, and gone once the press behind it has ended the rest.
  const elsewhere = useElsewhere(
    signed,
    reload,
    () => open({ kind: 'elsewhere' }),
    () => setOpened(null),
  );

  const sheet =
    opened === null ? undefined : opened.kind === 'elsewhere' ? (
      <ElsewhereSheet
        signedInWith={signed.signedInWith}
        onProved={elsewhere.proved}
        onClose={() => {
          setOpened(null);
          setFocusTo({ opener: ELSEWHERE });
        }}
      />
    ) : (
      drawSheet(opened, place, sheetProps(keyOf(opened)))
    );

  const asks: Asks = { sentNow, opener, sayingAt, open };

  return (
    <Shell at={null} payments={place.household.payments} over={sheet}>
      <AccountPage heading={client ? ACCOUNT.peopleAndSignIn : ACCOUNT.signIn} />
      {client ? (
        <Household place={place} asks={asks} local={local} />
      ) : (
        <MemberRow place={place} asks={asks} />
      )}
      <SignedIn place={place} signed={signed} elsewhere={elsewhere} buttonRef={opener(ELSEWHERE)} />
    </Shell>
  );
}

/** The sheet an ask opens, about the person or the household it names. */
function drawSheet(opened: AskOpened, place: PeoplePlace, props: PeopleSheetProps): ReactNode {
  // The person, as the page now lists her, or as the row drew her where a read since no longer
  // does, gone or never hers to ask about (the owner, 2026-10-20).
  const person =
    opened.kind === 'change' || opened.kind === 'remove'
      ? (place.people?.find((each) => each.ref === opened.ref) ?? opened.seen)
      : undefined;
  switch (opened.kind) {
    case 'add':
      return <AddPersonSheet {...props} homes={place.homes} />;
    case 'contact':
      return <ContactSheet {...props} name={place.you.name} />;
    case 'leave':
      return <LeaveSheet {...props} lastClient={place.you.lastClient === true} />;
    case 'change':
      return person === undefined ? null : (
        <ChangeHomesSheet
          {...props}
          name={person.name}
          personRef={person.ref}
          current={person.homeIds}
          homes={place.homes}
        />
      );
    case 'remove':
      return person === undefined ? null : (
        <TakeOffSheet {...props} name={person.name} personRef={person.ref} />
      );
  }
}

/** What a row needs to draw its asks and where each stands. */
interface Asks {
  /** Whether the door has just written this ask from this page. */
  readonly sentNow: (key: string) => boolean;
  readonly opener: (key: string) => (button: HTMLButtonElement | null) => void;
  readonly sayingAt: (key: string) => (element: HTMLElement | null) => void;
  readonly open: (opened: Opened) => void;
}

/**
 * Where one of her asks stands: what she asked, then where ops has it, in a block that can take the
 * focus once it is sent. Nothing where there is nothing to say of it.
 */
function AskSaid({
  askKey,
  asked,
  ask,
  sayingAt,
}: {
  readonly askKey: string;
  readonly asked: string;
  readonly ask: PeopleAsk | undefined;
  readonly sayingAt: Asks['sayingAt'];
}) {
  const line = ask === undefined ? null : askLine(ask);
  if (line === null) return null;
  return (
    <div ref={sayingAt(askKey)} tabIndex={-1} data-ask={askKey} className="mt-2">
      <p className={MUTED}>{asked}</p>
      <p className="text-small text-pretty text-text-body">
        {'lastClient' in line ? (
          <LastClient />
        ) : (
          <>
            {line.text}
            {line.phone ? (
              <>
                {' '}
                <Phone after="." />
              </>
            ) : null}
          </>
        )}
      </p>
    </div>
  );
}

/** An ask on a row, drawn as a link is, a box 48 tall, which opens its sheet. */
function RowAsk({
  words,
  onOpen,
  buttonRef,
}: {
  readonly words: string;
  readonly onOpen: () => void;
  readonly buttonRef: (button: HTMLButtonElement | null) => void;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-haspopup="dialog"
      onClick={onOpen}
      className={`cursor-pointer text-left text-small ${TEXT_LINK}`}
    >
      {words}
    </button>
  );
}

/** Her own row: her name, ops's word for her, her own two asks and where each stands. */
function YouRow({
  place,
  asks,
  client,
}: {
  readonly place: PeoplePlace;
  readonly asks: Asks;
  readonly client: boolean;
}) {
  const { you } = place;
  const line = personLine(you.householdRole, client ? 'client' : undefined);
  const contactAsk = asks.sentNow('contact') ? { state: 'sent' } : you.contactAsk;
  const leaveAsk = asks.sentNow('leave') ? { state: 'sent' } : you.leaveAsk;
  const changeOffered = you.offers.includes('person.contact_change') && !waiting(contactAsk);
  // Never to the household's only client: the Worker leaves the ask out of `offers` for her, since
  // ops would refuse it (`409 last_client`), and the button would lead only to saying so.
  const leaveOffered = you.offers.includes('person.leave') && !waiting(leaveAsk);
  return (
    <li data-you className={ROW}>
      <p className={NAME}>{yourName(you.name)}</p>
      {line === null ? null : <p className={LINE}>{line}</p>}
      <AskSaid
        askKey="contact"
        asked={PEOPLE.askedToChangeMine}
        ask={contactAsk}
        sayingAt={asks.sayingAt}
      />
      <AskSaid
        askKey="leave"
        asked={PEOPLE.askedToTakeMeOff}
        ask={leaveAsk}
        sayingAt={asks.sayingAt}
      />
      {changeOffered || leaveOffered ? (
        <div className="mt-1 flex flex-wrap gap-x-6">
          {changeOffered ? (
            <RowAsk
              words={PEOPLE.changeMine}
              onOpen={() => asks.open({ kind: 'contact' })}
              buttonRef={asks.opener('contact')}
            />
          ) : null}
          {leaveOffered ? (
            <RowAsk
              words={PEOPLE.takeMeOff}
              onOpen={() => asks.open({ kind: 'leave' })}
              buttonRef={asks.opener('leave')}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** Whether a person can sign in here, was invited and when, or neither, in words. */
function signInLine(person: PersonRow): string {
  switch (person.signIn) {
    case 'granted':
      return PEOPLE.canSignIn;
    case 'invited':
      return invitedLine(person.invitedOn);
    case 'none':
      return PEOPLE.notInvited;
  }
}

/** Her ask about a person of each kind, in the order a row offers them. */
const ROW_KINDS = ['change', 'remove'] as const;

type RowKind = (typeof ROW_KINDS)[number];

/** The seam's type of each kind of ask about a person. */
const ROW_TYPES: Readonly<Record<RowKind, string>> = {
  change: 'person.change',
  remove: 'person.remove',
};

/**
 * Her asks about this person that the page says, one of each kind: the one the door has just
 * written from this page, as sent, else her newest as the Worker gives it.
 */
function rowAsksOf(person: PersonRow, asks: Asks): Map<RowKind, PeopleAsk> {
  const said = new Map<RowKind, PeopleAsk>();
  for (const kind of ROW_KINDS) {
    if (asks.sentNow(`${kind}:${person.ref}`)) {
      said.set(kind, { state: 'sent' });
      continue;
    }
    const shown = person.asks?.find((ask) => ROW_ASKS[ask.type]?.kind === kind);
    if (shown !== undefined) said.set(kind, shown);
  }
  return said;
}

/**
 * Another person's row: her name, her line, whether she can sign in, each of her asks about her,
 * and what she may ask, or, where her own ask about somebody else holds an ask back, that she can
 * ask it once the office has answered hers.
 */
function PersonRowDrawn({
  person,
  place,
  asks,
  holders,
}: {
  readonly person: PersonRow;
  readonly place: PeoplePlace;
  readonly asks: Asks;
  /** Who her own waiting ask of each kind is about, by name, where she has one. */
  readonly holders: ReadonlyMap<RowKind, PersonRow>;
}) {
  const line = personLine(person.householdRole, person.door, person.homes);
  const said = rowAsksOf(person, asks);
  const pending = (kind: RowKind) => waiting(said.get(kind));
  const changeOffered =
    person.offers.includes('person.change') && place.homes.length > 0 && !pending('change');
  const removeOffered = person.offers.includes('person.remove') && !pending('remove');
  // Held back on this row by her own waiting ask of the kind about somebody else.
  const heldHere = ROW_KINDS.flatMap((kind) => {
    const holder = holders.get(kind);
    return (person.held ?? []).includes(ROW_TYPES[kind]) &&
      !pending(kind) &&
      holder !== undefined &&
      holder.ref !== person.ref
      ? [{ kind, holder }]
      : [];
  });
  const changeKey = `change:${person.ref}`;
  const removeKey = `remove:${person.ref}`;
  return (
    <li className={ROW}>
      {person.name === undefined ? null : <p className={NAME}>{person.name}</p>}
      {line === null ? null : <p className={LINE}>{line}</p>}
      <p className={MUTED}>{signInLine(person)}</p>
      {ROW_KINDS.map((kind) => {
        const ask = said.get(kind);
        return ask === undefined ? null : (
          <AskSaid
            key={kind}
            askKey={`${kind}:${person.ref}`}
            asked={kind === 'change' ? PEOPLE.askedToChange : PEOPLE.askedToTakeOff}
            ask={ask}
            sayingAt={asks.sayingAt}
          />
        );
      })}
      {heldHere.map(({ kind, holder }) => (
        <p key={kind} className={`mt-2 ${MUTED}`}>
          {kind === 'change' ? changeAsWell(holder.name) : takeOffAsWell(holder.name)}
        </p>
      ))}
      {changeOffered || removeOffered ? (
        <div className="mt-1 flex flex-wrap gap-x-6">
          {changeOffered ? (
            <RowAsk
              words={PEOPLE.changeWhatTheySee}
              onOpen={() => asks.open({ kind: 'change', ref: person.ref, seen: person })}
              buttonRef={asks.opener(changeKey)}
            />
          ) : null}
          {removeOffered ? (
            <RowAsk
              words={PEOPLE.takeThemOff}
              onOpen={() => asks.open({ kind: 'remove', ref: person.ref, seen: person })}
              buttonRef={asks.opener(removeKey)}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Your household, a client's alone: her own row, everybody else's, what waits on the office about
 * them, her own ask to add someone, and the button that asks.
 */
function Household({
  place,
  asks,
  local,
}: {
  readonly place: PeoplePlace;
  readonly asks: Asks;
  readonly local: SentHere | null;
}) {
  const headingId = useId();
  const people = place.people;
  const addAsk = asks.sentNow('add') ? { state: 'sent', name: local?.name } : place.addAsk;
  const addWaiting = waiting(addAsk);
  // Who her own waiting ask of each kind is about: ops holds one of each kind a household, so a row
  // that may not ask it now says it waits on hers, by the name of the person hers is about.
  const holders = new Map<RowKind, PersonRow>();
  for (const person of people ?? []) {
    const said = rowAsksOf(person, asks);
    for (const kind of ROW_KINDS) {
      if (waiting(said.get(kind)) && !holders.has(kind)) holders.set(kind, person);
    }
  }
  // Somebody else's ask holds a kind where she has none of her own waiting to say why.
  const aboutHeld = ROW_KINDS.some(
    (kind) => place.held.includes(ROW_TYPES[kind]) && !holders.has(kind),
  );
  const addOffered = place.offers.includes('person.add') && place.homes.length > 0 && !addWaiting;
  const addHeld = place.held.includes('person.add') && !addWaiting;
  return (
    <section data-household aria-labelledby={headingId} className="flex flex-col">
      <h2 id={headingId} className={SECTION_HEADING}>
        {PEOPLE.household}
      </h2>
      {people === undefined ? (
        <p className="mt-2 text-body text-pretty">{REST_ON_ITS_WAY}</p>
      ) : (
        <>
          <ul data-people className={`mt-3 ${CARD}`}>
            <YouRow place={place} asks={asks} client />
            {people.map((person) => (
              <PersonRowDrawn
                key={person.ref}
                person={person}
                place={place}
                asks={asks}
                holders={holders}
              />
            ))}
          </ul>
          {aboutHeld ? <p className={`mt-3 ${MUTED}`}>{PEOPLE.aboutHeld}</p> : null}
          <AskSaid
            askKey="add"
            asked={youAskedToAdd(addAsk?.name)}
            ask={addAsk}
            sayingAt={asks.sayingAt}
          />
          {addWaiting ? <p className={`mt-2 ${MUTED}`}>{PEOPLE.addAgainLater}</p> : null}
          {addOffered ? (
            <button
              ref={asks.opener('add')}
              type="button"
              aria-haspopup="dialog"
              onClick={() => asks.open({ kind: 'add' })}
              className={`mt-4 cursor-pointer ${GHOST_BUTTON}`}
            >
              {PEOPLE.add}
            </button>
          ) : addHeld ? (
            <p className={`mt-3 ${MUTED}`}>{PEOPLE.addHeld}</p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * A member's own row, and nobody else's: drawn while she has an ask to make or one to be told of,
 * which before ops grants her she has not.
 */
function MemberRow({ place, asks }: { readonly place: PeoplePlace; readonly asks: Asks }) {
  const { you } = place;
  const shown =
    you.offers.length > 0 ||
    you.lastClient === true ||
    you.contactAsk !== undefined ||
    you.leaveAsk !== undefined ||
    asks.sentNow('contact') ||
    asks.sentNow('leave');
  if (!shown) return null;
  return (
    <ul className={CARD}>
      <YouRow place={place} asks={asks} client={false} />
    </ul>
  );
}

/** What signing out everywhere else last came to, and a count, so each is said afresh. */
interface Ended {
  readonly what: 'done' | 'failed';
  readonly count: number;
}

/** Signing out everywhere else: what it last came to, its press, and that press again behind a code. */
interface Elsewhere {
  readonly ended: Ended | null;
  /** The line that says it was done, which takes the focus each time it is said. */
  readonly doneLine: RefObject<HTMLParagraphElement | null>;
  /** The button's press: to the Worker first, which asks for a fresh code where none stands. */
  readonly press: () => void;
  /** Once the code works: the same press again, and what the code step says if it does not go. */
  readonly proved: () => Promise<ProvedCame>;
}

/**
 * Signing out everywhere else (slice A3), behind a fresh code (slice P2): one press at a time; the
 * Worker's `403 step_up_required` opens the code step (`onCode`), and says none stands in every
 * tab; once it is done the sheet goes (`onDone`), and "Signed out everywhere else." is said with the
 * focus and the list read again; a failure on the page is said with the phone, and one behind the
 * code by the code step; a 401 is the door.
 */
function useElsewhere(
  signed: Signed,
  reload: () => void,
  onCode: () => void,
  onDone: () => void,
): Elsewhere {
  const [ended, setEnded] = useState<Ended | null>(null);
  const [focusDone, setFocusDone] = useState(0);
  const out = useRef(false);
  const count = useRef(0);
  const mounted = useRef(true);
  const doneLine = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (focusDone > 0) doneLine.current?.focus();
  }, [focusDone]);

  const say = (what: Ended['what']) => {
    count.current += 1;
    setEnded({ what, count: count.current });
  };

  /** Sends it, one at a time, and does what the Worker's answer asks of the page. */
  async function send(): Promise<OthersSignedOut | null> {
    if (out.current) return null;
    out.current = true;
    const answer = await signOutOthers();
    out.current = false;
    if (answer === 'signed_out') {
      signed.onSignedOut();
      return null;
    }
    if (answer === 'step_up') noFreshCode();
    if (!mounted.current) return null;
    if (answer === 'done') {
      onDone();
      say('done');
      setFocusDone((times) => times + 1);
      reload();
    }
    return answer;
  }

  async function press() {
    if (out.current) return;
    setEnded(null);
    const answer = await send();
    if (answer === 'step_up') onCode();
    else if (answer === 'failed') say('failed');
  }

  async function proved(): Promise<ProvedCame> {
    const answer = await send();
    return answer === 'step_up' || answer === 'failed' ? answer : null;
  }

  return { ended, doneLine, press: () => void press(), proved };
}

/**
 * The code step of signing out everywhere else (slice P2), in a sheet named by the button, as each
 * ask's is: why first, then the other asks' code step (`FreshCode.tsx`), whose button signs out
 * everywhere else once the code is typed. The step is drawn once the sheet has taken the focus to
 * its name, so the cursor then goes to the field, as on every code step.
 */
function ElsewhereSheet({
  signedInWith,
  onProved,
  onClose,
}: {
  readonly signedInWith: SignedInWith;
  readonly onProved: () => Promise<ProvedCame>;
  readonly onClose: () => void;
}) {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => setDrawn(true), []);
  return (
    <Sheet heading={PEOPLE.signOutElsewhere} onClose={onClose}>
      {drawn ? (
        <FreshCode
          signedInWith={signedInWith}
          why={PEOPLE.signOutElsewhereFreshCode}
          submit={PEOPLE.signOutElsewhere}
          closedBeforePhone={PEOPLE.signOutElsewhereCannotBeforePhone}
          onProved={onProved}
          className="mt-4"
        />
      ) : null}
    </Sheet>
  );
}

/** A phone or computer she is signed in on: its words, then its days. */
function SessionRow({ session, line }: { readonly session: SessionShown; readonly line: string }) {
  return (
    <li className={ROW}>
      <p className={NAME}>{deviceWords(session.browser, session.system)}</p>
      <p className={`mt-0.5 ${MUTED}`}>{line}</p>
    </li>
  );
}

/**
 * Where she is signed in: this one, then the others and how many more; "Sign out everywhere else"
 * while another is live, else "Signed in here only."; and "Sign out here".
 */
function SignedIn({
  place,
  signed,
  elsewhere,
  buttonRef,
}: {
  readonly place: PeoplePlace;
  readonly signed: Signed;
  readonly elsewhere: Elsewhere;
  readonly buttonRef: (button: HTMLButtonElement | null) => void;
}) {
  const headingId = useId();
  const { here, others, more, moreAtLeast } = place.signedIn;
  const { ended, doneLine } = elsewhere;
  const leaving = useSignOut(signed.onSignedOut);

  const listed = here === null ? others.length : others.length + 1;
  return (
    <section data-signed-in aria-labelledby={headingId} className="flex flex-col">
      <h2 id={headingId} className={SECTION_HEADING}>
        {PEOPLE.whereSignedIn}
      </h2>
      <p className={`mt-1.5 ${MUTED}`}>
        {signed.signedInWith.channel === 'text' ? PEOPLE.noPasswordByText : PEOPLE.noPassword}
      </p>
      {listed === 0 ? null : (
        <ul className={`mt-3 ${CARD}`}>
          {here === null ? null : <SessionRow session={here} line={thisOne(here.signedIn)} />}
          {others.map((session, index) => (
            <SessionRow
              key={`${index}-${session.signedIn}-${session.lastUsed}`}
              session={session}
              line={usedLine(session.signedIn, session.lastUsed, place.today)}
            />
          ))}
        </ul>
      )}
      {more > 0 ? <p className={`mt-2 ${MUTED}`}>{andMore(more, moreAtLeast === true)}</p> : null}
      <div aria-live="polite">
        {ended === null ? null : ended.what === 'done' ? (
          <p
            key={ended.count}
            ref={doneLine}
            tabIndex={-1}
            className="mt-3 text-body text-text-body"
          >
            {PEOPLE.signedOutElsewhere}
          </p>
        ) : (
          <p key={ended.count} className="mt-3 text-body text-pretty text-text-body">
            {FAILED_BEFORE_PHONE} <Phone after="." />
          </p>
        )}
      </div>
      {others.length > 0 ? (
        <button
          ref={buttonRef}
          type="button"
          onClick={elsewhere.press}
          className={`mt-4 cursor-pointer ${GHOST_BUTTON}`}
        >
          {PEOPLE.signOutElsewhere}
        </button>
      ) : ended?.what === 'done' ? null : (
        <p className="mt-3 text-body text-text-body">{ACCOUNT.signedInHereOnly}</p>
      )}
      <button
        type="button"
        onClick={leaving.signOut}
        className={`mt-1 cursor-pointer self-start text-body ${TEXT_LINK}`}
      >
        {PEOPLE.signOutHere}
      </button>
      <SignOutFailed failed={leaving.failed} />
    </section>
  );
}
